// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analytics, EVENTS } from '@/lib/analytics';

export type TimeRangePreset = '1' | '7' | '30' | '90' | '180' | '365';
export type TrendGranularity = 'daily' | 'weekly' | 'monthly';

interface TimeFilterState {
  // Time range preset
  timeRangePreset: TimeRangePreset;

  // Exclude bots filter
  excludeBots: boolean;

  // Selected repository filter (null = all repositories)
  selectedRepoId: string | null;

  // Selected team filter (null = all teams)
  selectedTeamId: string | null;

  // Trend chart granularity
  trendGranularity: TrendGranularity;

  // Actions
  setTimeRangePreset: (preset: TimeRangePreset) => void;
  setExcludeBots: (exclude: boolean) => void;
  setSelectedRepoId: (repoId: string | null) => void;
  setSelectedTeamId: (teamId: string | null) => void;
  setTrendGranularity: (granularity: TrendGranularity) => void;

  // Helper methods
  getDateRange: () => { dateFrom: string; dateTo: string };
  getDaysCount: () => number;
}

export const useTimeFilterStore = create<TimeFilterState>()(
  persist(
    (set, get) => ({
      // Default to last 30 days
      timeRangePreset: '30',
      excludeBots: true,
      selectedRepoId: null,
      selectedTeamId: null,
      trendGranularity: 'daily',

      setTimeRangePreset: (preset: TimeRangePreset) => {
        const previousPreset = get().timeRangePreset;
        set({ timeRangePreset: preset });

        // Track time period change
        if (preset !== previousPreset) {
          const daysLabel = preset === '1' ? '1 day' : preset === '365' ? '1 year' : `${preset} days`;
          analytics.track({
            name: EVENTS.TIME_PERIOD_CHANGED,
            params: {
              time_period: preset,
              time_period_label: daysLabel,
              previous_period: previousPreset,
            },
          });
        }
      },

      setExcludeBots: (exclude: boolean) => {
        set({ excludeBots: exclude });
      },

      setSelectedRepoId: (repoId: string | null) => {
        // Scope is mutually exclusive: choosing a repo clears any team filter.
        set({ selectedRepoId: repoId, selectedTeamId: repoId ? null : get().selectedTeamId });
      },

      setSelectedTeamId: (teamId: string | null) => {
        // Scope is mutually exclusive: choosing a team clears any repo filter.
        set({ selectedTeamId: teamId, selectedRepoId: teamId ? null : get().selectedRepoId });
      },

      setTrendGranularity: (granularity: TrendGranularity) => {
        set({ trendGranularity: granularity });
      },

      // Get date range for API calls (date_from, date_to)
      getDateRange: () => {
        const preset = get().timeRangePreset;
        const days = parseInt(preset, 10);
        const dateTo = new Date();
        const dateFrom = new Date();
        dateFrom.setDate(dateFrom.getDate() - days);

        return {
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
        };
      },

      // Get days count for trend API calls
      getDaysCount: () => {
        const preset = get().timeRangePreset;
        return parseInt(preset, 10);
      },
    }),
    {
      name: 'time-filter-storage',
    }
  )
);